import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert
} from '@mui/material';
import { supabase } from '../../utils/supabase';

const CATEGORIES = [
  'Vegetables',
  'Fruits',
  'Meat',
  'Dairy',
  'Grains',
  'Legumes',
  'Herbs & Spices',
  'Beverages',
  'Condiments',
  'Snacks',
  'Frozen',
  'Canned',
  'Other'
];

const UNITS = [
  'pieces',
  'cups',
  'tablespoons',
  'teaspoons',
  'pounds',
  'ounces',
  'grams',
  'kilograms',
  'litres',
  'millilitres',
  'packages',
  'bottles',
  'cans'
];

export default function IngredientForm({ open, onClose, onSave, ingredient, session }) {
  const [formData, setFormData] = useState({
    name: '',
    quantity: '',
    unit: 'pieces',
    category: '',
    expiry_date: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (ingredient) {
      setFormData({
        name: ingredient.name || '',
        quantity: ingredient.quantity || '',
        unit: ingredient.unit || 'pieces',
        category: ingredient.category || '',
        expiry_date: ingredient.expiry_date ? ingredient.expiry_date.split('T')[0] : '',
        notes: ingredient.notes || ''
      });
    } else {
      setFormData({
        name: '',
        quantity: '',
        unit: 'pieces',
        category: '',
        expiry_date: '',
        notes: ''
      });
    }
    setError(null);
  }, [ingredient, open]);

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      setError('Ingredient name is required');
      return;
    }
    
    if (!formData.quantity || formData.quantity <= 0) {
      setError('Please enter a valid quantity');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const ingredientData = {
        name: formData.name.trim(),
        quantity: parseFloat(formData.quantity),
        unit: formData.unit,
        category: formData.category || null,
        expiry_date: formData.expiry_date || null,
        notes: formData.notes.trim() || null,
        user_id: session?.user?.id
      };

      let result;
      if (ingredient) {
        // Update existing ingredient using REST API
        const url = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/ingredients?id=eq.${ingredient.id}`;
        
        const response = await fetch(url, {
          method: 'PATCH',
          headers: {
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
            'Prefer': 'return=representation'
          },
          body: JSON.stringify(ingredientData)
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        result = { error: null };
      } else {
        // Create new ingredient using REST API
        const url = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/ingredients`;
        
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
            'Prefer': 'return=representation'
          },
          body: JSON.stringify(ingredientData)
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        result = { error: null };
      }

      if (result.error) throw result.error;

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving ingredient:', error);
      setError(error.message || 'Failed to save ingredient');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {ingredient ? 'Edit Ingredient' : 'Add New Ingredient'}
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Ingredient Name"
              value={formData.name}
              onChange={handleChange('name')}
              required
              fullWidth
              placeholder="e.g., Tomatoes, Chicken Breast, Milk"
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Quantity"
                type="number"
                value={formData.quantity}
                onChange={handleChange('quantity')}
                required
                inputProps={{ min: 0, step: 0.1 }}
                sx={{ flex: 1 }}
              />
              
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={formData.unit}
                  onChange={handleChange('unit')}
                  label="Unit"
                >
                  {UNITS.map((unit) => (
                    <MenuItem key={unit} value={unit}>
                      {unit}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                onChange={handleChange('category')}
                label="Category"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {CATEGORIES.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              label="Expiry Date"
              type="date"
              value={formData.expiry_date}
              onChange={handleChange('expiry_date')}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            
            <TextField
              label="Notes"
              value={formData.notes}
              onChange={handleChange('notes')}
              multiline
              rows={2}
              fullWidth
              placeholder="Optional notes about this ingredient"
            />
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
          >
            {loading ? 'Saving...' : (ingredient ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}